import React from "react";
import { useTranslation } from "../../../contexts/I18nProvider";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuShortcut,
  ContextMenuTrigger,
} from "../../../components/ui/context-menu";
import { useSelector } from "react-redux";
import { selectSearchQuery } from "../../../redux/selectors/filterSelectors";

const ParagraphCanvas = ({
  sentence, // Object containing the sentence details
  shouldBeVisible, // Boolean to control visibility
  playAudio, // Function to play audio
  canPlayAudio = true,
  maxVisibleWords = null,
  MAX_SENTENCES,
  africanOnly, // Boolean to control visibility of African-only sentences
  colorIndex, // Color index from parent card
}) => {
  const { t } = useTranslation();
  const sentenceSearchQuery = useSelector(selectSearchQuery);

  // Parse sentence parts
  const parseSentenceParts = (text) => {
    if (!text) return { english: "", targetLanguage: "", french: "" };
    
    const parts = text.split("|").map((part) => part.trim());
    
    return {
      english: parts[0] || "",
      targetLanguage: parts[1] || "",
      french: parts[2] || "",
    };
  };

  const { english, targetLanguage, french } = parseSentenceParts(sentence.text);

  const renderTextWithHighlight = (text) => {
    if (!sentenceSearchQuery || !text) return text;

    const parts = text.split(
      new RegExp(`(${sentenceSearchQuery})`, "gi")
    );

    return parts.map((part, index) =>
      part.toLowerCase() === sentenceSearchQuery?.toLowerCase() ? (
        <span
          key={index}
          className="bg-emerald-400 rounded px-1"
        >
          {part}
        </span>
      ) : (
        <span key={index}>{part}</span>
      )
    );
  };

  const handleClick = () => {
    if (shouldBeVisible && canPlayAudio && sentence) {
      if (!sentence.audio_path) {
        console.warn("No audio path available for sentence:", sentence.id);
        return;
      }
      console.log("Playing audio for sentence:", sentence.id, sentence.audio_path);
      playAudio(sentence.audio_path);
    }
  };


  return (
    <div className="w-full polyglot-canvas">
      <ContextMenu>
        <ContextMenuTrigger
          className={`flex w-full flex-col hover:bg-primary/10 border border-transparent hover:border-primary/10 pb-4 px-4 -mx-3 py-4 transition-colors duration-150 rounded-xl ease-in-out ${
            shouldBeVisible ? "cursor-pointer" : "cursor-not-allowed"
          }`}
          style={{ userSelect: "none", opacity: shouldBeVisible ? 1 : 0.35 }}
          onClick={handleClick}
        >
          {/* Sentence counter and content */}
          <div className="flex flex-col gap-3">
            {/* English Part */}
            {english && (
              <div className="space-y-1">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  {t('polyglot.englishLabel')}
                </div>
                <div className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                  {sentence.sentence_counter}. {english}
                </div>
              </div>
            )}

            {/* Target Language Part - Highlighted */}
            {targetLanguage && (
              <div className="space-y-1">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  {sentence.language_name || t('polyglot.targetLanguageLabel')}
                </div>
                <div 
                  className="text-base rounded-lg p-3 leading-relaxed bg-gray-200 polyglot-charis dark:bg-gray-700/40" 
                >
                  {renderTextWithHighlight(targetLanguage)}
                </div>
              </div>
            )}

            {/* French Part */}
            {french && (
              <div className="space-y-1">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  {t('polyglot.frenchLabel')}
                </div>
                <div className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                  {french}
                </div>
              </div>
            )}
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-64 border-[0.5px] border-gray-400 dark:border-gray-700">
          <ContextMenuItem inset className="hover:bg-gray-200 dark:hover:bg-gray-800 cursor-pointer">
            {t('buttons.back')}
            <ContextMenuShortcut>⌘[</ContextMenuShortcut>
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    </div>
  );
};

export default ParagraphCanvas;
